import type { TourDetailData } from "@/api/tour";
import VerticalImageList from "./VerticalImageList";
import { stripHtml } from "@/utils/format";

interface BasicInfoProps {
  detail: TourDetailData;
  onBack?: () => void;
}

export default function BasicInfo({ detail, onBack }: BasicInfoProps) {
  const hasOldPrice = detail.old_price && Number(detail.old_price) > Number(detail.price);

  return (
    <div className="bg-white px-5 pt-3 pb-5 rounded-b-3xl mb-2 overflow-hidden" style={{ boxShadow: "0 2px 12px rgba(0,0,0,0.04)" }}>
      {/* 返回按钮（卡片左上角，融入白色背景） */}
      {onBack && (
        <div className="mb-2">
          <button
            onClick={onBack}
            aria-label="返回"
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer active:scale-90 transition-transform"
            style={{ background: "#F3F4F6" }}
          >
            <i className="ri-arrow-left-line text-gray-700" style={{ fontSize: 18 }} />
          </button>
        </div>
      )}

      {/* Title */}
      <h1 className="text-[22px] font-bold text-gray-900 leading-tight">{stripHtml(detail.title)}</h1>

      {/* Stats row */}
      <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
        <span className="flex items-center gap-1">
          <i className="ri-archive-line" />
          库存 {detail.stock ?? "-"}
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-flight-takeoff-line" />
          出发 {stripHtml(detail.departure_city) || "-"}
        </span>
        {detail.sold_count !== undefined && detail.sold_count > 0 && (
          <span className="flex items-center gap-1" style={{ color: "#F97316" }}>
            <i className="ri-fire-line" />
            已售 {detail.sold_count}
          </span>
        )}
      </div>

      {/* Tags（位于库存下方） */}
      <div className="flex items-center gap-2 mt-3 flex-wrap">
        <span
          className="text-[11px] px-2.5 py-1 rounded-full font-medium flex items-center gap-1"
          style={{ background: "#FFF7ED", color: "#EA580C" }}
        >
          <i className="ri-time-line" />
          {detail.days_text}
        </span>
        {detail.destinations && (
          <span
            className="text-[11px] px-2.5 py-1 rounded-full font-medium flex items-center gap-1"
            style={{ background: "#F0FDF4", color: "#166534" }}
          >
            <i className="ri-map-pin-line" />
            {stripHtml(detail.destinations)}
          </span>
        )}
        {detail.group_people > 0 && (
          <span
            className="text-[11px] px-2.5 py-1 rounded-full font-medium flex items-center gap-1"
            style={{ background: "#FEF3C7", color: "#92400E" }}
          >
            <i className="ri-user-3-line" />
            {detail.group_people}人成团
          </span>
        )}
      </div>

      {/* 竖向图片列表（按 API 返回顺序自上而下排列） */}
      <VerticalImageList images={detail.images && detail.images.length > 0 ? detail.images : [detail.cover_image]} />

      {/* Price card（图片下方） */}
      <div
        className="mt-4 rounded-2xl p-4 flex items-end justify-between"
        style={{ background: "linear-gradient(135deg, #FFF7ED 0%, #FFF0F5 100%)" }}
      >
        <div>
          <div className="text-xs text-gray-500 mb-1">成人价</div>
          <div className="flex items-baseline gap-1">
            <span className="text-xs font-bold" style={{ color: "#EF4444" }}>¥</span>
            <span className="text-2xl font-bold" style={{ color: "#EF4444" }}>{detail.price}</span>
            <span className="text-xs text-gray-400">/人起</span>
            {hasOldPrice && (
              <span className="text-xs text-gray-400 line-through ml-1.5">¥{detail.old_price}</span>
            )}
          </div>
        </div>
        {detail.child_price && Number(detail.child_price) > 0 && (
          <div className="text-right">
            <div className="text-xs text-gray-500 mb-1">儿童价</div>
            <div className="text-sm font-bold" style={{ color: "#F59E0B" }}>
              ¥{detail.child_price}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
